import { Formik } from "formik";
import { Building2, Phone, UserRound } from "lucide-react";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { EinField, PhoneField } from "@/components/common/masked-form-field";
import { Card } from "@/components/ui/card";
import type { Client, UpdateClientPayload } from "@/features/clients/types";
import { EIN_RE } from "@/lib/masks";
import {
  contactAddressInitialValues,
  contactAddressPayload,
  contactAddressRules,
  type ContactAddressValues,
  FIELD_LABEL,
  optionalMatch,
  type ProfileFormProps,
  useProfileSubmit,
} from "@/features/clients/components/profile-form-shared";
import {
  AddressSection,
  ContactMethodField,
  NotesSection,
  ProfileFormShell,
} from "@/features/clients/components/profile-form-sections";

const sections = [
  { id: "business-details", label: "Business Details" },
  { id: "contact-person", label: "Contact Person" },
  { id: "contact-details", label: "Contact Details" },
  { id: "address", label: "Business Address" },
  { id: "notes", label: "Notes" },
];

/** Business-specific values on top of the shared contact/address slice. */
interface BusinessProfileFormValues extends ContactAddressValues {
  businessName: string;
  dba: string;
  /** EIN — blank means "keep the stored value". */
  ein: string;
  /** ISO alpha-2 country code. */
  country: string;
  businessContactFirstName: string;
  businessContactLastName: string;
  businessContactTitle: string;
}

function toInitialValues(client: Client): BusinessProfileFormValues {
  return {
    ...contactAddressInitialValues(client),
    businessName: client.businessName ?? "",
    dba: client.dba ?? "",
    ein: "",
    country: client.country ?? "",
    businessContactFirstName: client.businessContactFirstName ?? "",
    businessContactLastName: client.businessContactLastName ?? "",
    businessContactTitle: client.businessContactTitle ?? "",
  };
}

/** Form values → PATCH body. The EIN is omitted when blank so a stored value is
 *  never blanked (the API can't echo the raw EIN back to prefill it). */
function toPayload(values: BusinessProfileFormValues): UpdateClientPayload {
  const payload: UpdateClientPayload = {
    ...contactAddressPayload(values),
    business_name: values.businessName,
    dba: values.dba,
    country: values.country,
    business_contact_first_name: values.businessContactFirstName,
    business_contact_last_name: values.businessContactLastName,
    business_contact_title: values.businessContactTitle,
  };
  if (values.ein) payload.ein = values.ein;
  return payload;
}

const schema = Yup.object({
  ...contactAddressRules,
  businessName: Yup.string().required("Business name is required"),
  ein: optionalMatch(EIN_RE, "Enter a valid EIN"),
  country: optionalMatch(/^[A-Za-z]{2}$/, "Use the 2-letter country code"),
});

/** Card header band matching the individual form's section cards. */
function SectionHeader({ icon: Icon, title }: { icon: typeof Building2; title: string }) {
  return (
    <div className="bg-muted/40 border-b px-6 py-4">
      <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
        <Icon className="text-primary size-5" /> {title}
      </h3>
    </div>
  );
}

/**
 * Business client profile form — no personal-name or spouse fields (a business
 * has neither). Formik owns state/submission, Yup owns validation. The EIN lives
 * in Formik state only and is never logged.
 */
export function BusinessProfileForm({
  client,
  onClientUpdated,
  extraSections,
}: ProfileFormProps) {
  const { handleSubmit, continueRef } = useProfileSubmit<BusinessProfileFormValues>({
    client,
    onClientUpdated,
    toPayload,
    toInitialValues,
  });

  return (
    <Formik<BusinessProfileFormValues>
      initialValues={toInitialValues(client)}
      validationSchema={schema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, resetForm }) => (
        <ProfileFormShell
          sections={sections}
          isSubmitting={isSubmitting}
          resetForm={resetForm}
          continueRef={continueRef}
          footer={extraSections}
        >
          {/* Business Details */}
          <Card id="business-details" className="scroll-mt-24 gap-0 overflow-hidden p-0">
            <SectionHeader icon={Building2} title="Business Details" />
            <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:grid-cols-3">
              <div className="md:col-span-2 lg:col-span-3">
                <FormField name="businessName" label="Business Name" labelClassName={FIELD_LABEL} />
              </div>
              <FormField name="dba" label="DBA" labelClassName={FIELD_LABEL} />
              <div>
                <EinField name="ein" label="Employer ID (EIN)" labelClassName={FIELD_LABEL} />
                <p className="text-muted-foreground mt-1 text-xs">
                  Current: {client.idLabel || "—"}. Leave blank to keep unchanged.
                </p>
              </div>
              <FormField
                name="email"
                label="Email Address"
                type="email"
                labelClassName={FIELD_LABEL}
              />
              <FormField
                name="country"
                label="Country"
                labelClassName={FIELD_LABEL}
                maxLength={2}
              />
            </div>
          </Card>

          {/* Contact Person — the human the firm deals with at the business. */}
          <Card id="contact-person" className="scroll-mt-24 gap-0 overflow-hidden p-0">
            <SectionHeader icon={UserRound} title="Contact Person" />
            <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:grid-cols-3">
              <FormField
                name="businessContactFirstName"
                label="First Name"
                labelClassName={FIELD_LABEL}
              />
              <FormField
                name="businessContactLastName"
                label="Last Name"
                labelClassName={FIELD_LABEL}
              />
              <FormField name="businessContactTitle" label="Title" labelClassName={FIELD_LABEL} />
            </div>
          </Card>

          {/* Contact Details */}
          <Card id="contact-details" className="scroll-mt-24 gap-0 overflow-hidden p-0">
            <SectionHeader icon={Phone} title="Contact Details" />
            <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:grid-cols-3">
              <PhoneField name="workPhone" label="Work Phone" labelClassName={FIELD_LABEL} />
              <PhoneField name="mobilePhone" label="Mobile Phone" labelClassName={FIELD_LABEL} />
              <PhoneField name="faxNumber" label="Fax Number" labelClassName={FIELD_LABEL} />
              <ContactMethodField name="preferredContactMethod" label="Preferred Contact Method" />
            </div>
          </Card>

          <AddressSection title="Business Address" />

          <NotesSection />
        </ProfileFormShell>
      )}
    </Formik>
  );
}
